import { ThemeEvents } from '@theme/events';

const AUTO_DISMISS_MS = 3000;
const APPROACHING_RATIO = 0.8;

// Sources whose cart:update events should pop this toast. Excludes e.g.
// cart-items-component (qty change / remove in the cart drawer), which
// dispatches the same event but shouldn't surface an "Added to Cart" toast.
const TOAST_SOURCES = ['product-form-component', 'bulk-order-workspace'];

/**
 * Toast after successful add-to-cart from product forms. Listens for cart:update from product-form-component.
 */
class AddToCartToast extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {AbortController | undefined} */
  #cartAbort;
  /** @type {number} */
  #hideTimer = 0;
  /** @type {number} */
  #teardownTimer = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const closeBtn = this.querySelector('.add-to-cart-toast__close');
    closeBtn?.addEventListener('click', this.#dismiss, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
    this.#cartAbort?.abort();
    this.#cartAbort = undefined;
    this.#clearTimers();
  }

  /** @param {Event} event */
  #onCartUpdate = async (event) => {
    const data = /** @type {CustomEvent} */ (event).detail?.data;
    if (!TOAST_SOURCES.includes(data?.source) || data?.didError) return;

    const statusReady = await this.#syncStatusFromCart();
    if (!statusReady) return;

    this.#show();
  };

  /**
   * Recomputes the B2B credit status from the live cart total, then the toast may show.
   * @returns {Promise<boolean>} false if a newer add aborted this fetch; true otherwise
   */
  async #syncStatusFromCart() {
    const creditLimit = Number(this.dataset.creditLimit);
    const creditUsage = Number(this.dataset.creditUsage);
    if (!Number.isFinite(creditLimit) || !Number.isFinite(creditUsage)) return true;

    this.#cartAbort?.abort();
    this.#cartAbort = new AbortController();
    const { signal } = this.#cartAbort;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) return true;

      const cart = await response.json();
      const totalPrice = Number(cart.total_price);
      if (!Number.isFinite(totalPrice)) return true;

      this.#applyStatus(creditUsage + totalPrice, creditLimit);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return false;
    }

    return true;
  }

  /**
   * @param {number} consumed
   * @param {number} creditLimit
   */
  #applyStatus(consumed, creditLimit) {
    const statusEl = this.querySelector('.add-to-cart-toast__status-message');
    if (!(statusEl instanceof HTMLElement)) return;

    const approachingThreshold = creditLimit * APPROACHING_RATIO;
    let message = '';
    if (consumed > creditLimit) {
      message = this.dataset.statusExceeded ?? '';
    } else if (consumed >= approachingThreshold) {
      message = this.dataset.statusApproaching ?? '';
    }

    statusEl.textContent = message;
  }

  #show() {
    this.#clearTimers();

    this.hidden = false;
    this.classList.add('add-to-cart-toast--visible');

    const bar = this.querySelector('.add-to-cart-toast__progress-bar');
    if (bar instanceof HTMLElement) {
      bar.classList.remove('add-to-cart-toast__progress-bar--animating');
      void bar.offsetWidth;
      bar.classList.add('add-to-cart-toast__progress-bar--animating');
    }

    this.#hideTimer = window.setTimeout(this.#dismiss, AUTO_DISMISS_MS);
  }

  #dismiss = () => {
    this.#clearHideTimer();
    this.classList.remove('add-to-cart-toast--visible');

    if (this.#teardownTimer) clearTimeout(this.#teardownTimer);
    const duration = prefersReducedMotion() ? 0 : 200;
    this.#teardownTimer = window.setTimeout(() => {
      this.hidden = true;
      this.#teardownTimer = 0;
    }, duration);
  };

  #clearHideTimer() {
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#hideTimer = 0;
  }

  #clearTimers() {
    this.#clearHideTimer();
    if (this.#teardownTimer) clearTimeout(this.#teardownTimer);
    this.#teardownTimer = 0;
  }
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

if (!customElements.get('add-to-cart-toast')) {
  customElements.define('add-to-cart-toast', AddToCartToast);
}